import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Download, FileText, LayoutGrid, LogOut, Package, Receipt, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, czk, errMsg, fmtDate, fmtSize, media, openBlob, STATUS } from "../lib/api";
import { useAuth } from "../lib/store";
import { Daisy } from "../components/Daisy";
import { useSeo } from "../components/Seo";

const TABS = [["prehled", "Přehled", LayoutGrid], ["objednavky", "Objednávky", Package], ["soubory", "Soubory", Download], ["faktury", "Faktury", Receipt], ["udaje", "Osobní údaje", UserRound]];

const Downloads = ({ list }) => {
  const dl = async (d, f) => {
    try {
      const { data } = await api.post(`/my/downloads/${d.order_id}/${d.product_id}/${f.id}/link`);
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!list.length) return <Empty text="Zatím tu nejsou žádné soubory. Po zaplacení objednávky se objeví zde." />;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {list.map((d) => (
        <div key={d.product_id} className="card-soft flex gap-4 p-4" data-testid={`download-item-${d.slug}`}>
          <img src={media(d.image)} alt="" className="h-20 w-20 rounded-2xl object-cover" />
          <div className="flex-1">
            <p className="font-semibold">{d.name}</p>
            <p className="text-xs text-[#6E7565]">Objednávka {d.order_number} · {fmtDate(d.paid_at)}</p>
            <div className="mt-2 space-y-1.5">
              {d.files.length ? d.files.map((f) => (
                <button key={f.id} onClick={() => dl(d, f)} className="flex w-full items-center gap-2 rounded-xl bg-[#E9EFE0] px-3 py-2 text-left text-sm transition-colors hover:bg-[#A3AC86] hover:text-white" data-testid={`download-file-${f.id}`}>
                  <FileText className="h-4 w-4" /> <span className="flex-1 truncate">{f.name}</span> <span className="text-xs">{fmtSize(f.size)}</span> <Download className="h-4 w-4" />
                </button>
              )) : <p className="text-xs text-[#6E7565]">Soubory budou brzy doplněny.</p>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Empty = ({ text }) => <div className="py-14 text-center"><Daisy size={70} className="mx-auto animate-wiggle" /><p className="mt-4 text-[#6E7565]">{text}</p></div>;

const OrdersList = ({ orders, invoices }) => {
  const list = invoices ? orders.filter((o) => o.invoice_number) : orders;
  if (!list.length) return <Empty text={invoices ? "Zatím nemáte žádné faktury." : "Zatím nemáte žádné objednávky."} />;
  const pdf = (o) => openBlob(api, `/invoices/${o.id}/pdf`, `faktura-${o.invoice_number}.pdf`).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-3">
      {list.map((o) => (
        <div key={o.id} className="card-soft flex flex-wrap items-center gap-4 p-4" data-testid={`order-row-${o.number}`}>
          <div className="flex-1">
            <p className="font-semibold">{invoices ? `Faktura ${o.invoice_number}` : `Objednávka ${o.number}`}</p>
            <p className="text-xs text-[#6E7565]">{fmtDate(o.created_at)} · {o.items.length} položek · {({ stripe: "Karta", comgate: "Comgate", voucher: "Dárkový poukaz", free: "Zdarma" })[o.payment_method] || "Převod"}</p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS[o.status].cls}`}>{STATUS[o.status].label}</span>
          <span className="w-24 text-right font-bold">{czk(o.total)}</span>
          {o.invoice_number && <button onClick={() => pdf(o)} className="btn-ghost !px-4 !py-2" data-testid={`invoice-download-${o.number}`}><Receipt className="h-4 w-4" /> PDF</button>}
          {!invoices && <Link to={`/objednavka/${o.id}`} className="btn-sage !px-4 !py-2" data-testid={`order-detail-${o.number}`}>{o.status === "pending" ? "Zaplatit" : "Detail"}</Link>}
        </div>
      ))}
    </div>
  );
};

const Profile = () => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState(user);
  const [pw, setPw] = useState({ old_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/auth/me", f);
      setUser(data);
      toast.success("Údaje uloženy");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Heslo změněno");
      setPw({ old_password: "", new_password: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const fld = (k, l) => <label className="block text-sm"><span className="mb-1 block font-medium">{l}</span><input className="input-soft" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`profile-${k}`} /></label>;
  return (
    <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
      <form onSubmit={save} className="card-soft grid gap-4 p-6 sm:grid-cols-2">
        {fld("name", "Jméno a příjmení")}{fld("phone", "Telefon")}{fld("street", "Ulice")}{fld("city", "Město")}{fld("zip", "PSČ")}{fld("company", "Firma")}{fld("ico", "IČO")}{fld("dic", "DIČ")}
        <button className="btn-sage sm:col-span-2" data-testid="profile-save-btn">Uložit údaje</button>
      </form>
      <form onSubmit={changePw} className="card-soft h-fit space-y-3 p-6">
        <p className="h-section text-xl">Změna hesla</p>
        {user.provider !== "google" && <input type="password" className="input-soft" placeholder="Původní heslo" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} data-testid="profile-old-password" />}
        <input type="password" minLength={6} required className="input-soft" placeholder="Nové heslo" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="profile-new-password" />
        <button className="btn-ghost w-full" data-testid="profile-password-btn">Změnit heslo</button>
      </form>
    </div>
  );
};

export default function Account() {
  useSeo({ title: "Můj účet" });
  const { user, logout } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "prehled";
  const [orders, setOrders] = useState([]);
  const [downloads, setDownloads] = useState([]);
  useEffect(() => {
    if (!user) return;
    api.get("/orders/my").then((r) => setOrders(r.data));
    api.get("/my/downloads").then((r) => setDownloads(r.data));
  }, [user]);
  if (user === undefined) return <div className="grid min-h-[50vh] place-items-center"><Daisy size={60} className="animate-spin" /></div>;
  if (!user) return <Navigate to="/prihlaseni" replace />;
  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8" data-testid="account-page">
      <div className="flex flex-wrap items-center gap-4">
        <span className="grid h-16 w-16 place-items-center rounded-full bg-[#A3AC86]"><Daisy size={46} /></span>
        <div className="flex-1"><p className="font-script text-2xl text-[#D96B43]">Ahoj,</p><h1 className="h-display text-4xl" data-testid="account-name">{user.name || user.email}</h1></div>
        <button onClick={logout} className="btn-ghost" data-testid="logout-btn"><LogOut className="h-4 w-4" /> Odhlásit</button>
      </div>
      <div className="no-scrollbar mt-8 flex gap-2 overflow-x-auto">
        {TABS.map(([id, l, I]) => (
          <button key={id} onClick={() => setSp({ tab: id })} data-testid={`account-tab-${id}`} className={`relative flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium ${tab === id ? "text-white" : "bg-white"}`}>
            {tab === id && <motion.span layoutId="acc-pill" className="absolute inset-0 rounded-full bg-[#7A8460]" />}
            <I className="relative h-4 w-4" /><span className="relative">{l}</span>
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-8">
          {tab === "prehled" && (
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {[["Objednávky", orders.length], ["Souborů ke stažení", downloads.reduce((s, d) => s + d.files.length, 0)]].map(([k, v], i) => (
                <motion.div key={k} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className={`rounded-3xl p-4 sm:p-6 ${i === 1 ? "bg-[#A3AC86] text-white" : "card-soft"}`}>
                  <p className="text-sm opacity-80">{k}</p><p className="mt-2 font-serif text-3xl font-black sm:text-4xl" data-testid={`account-stat-${i}`}>{v}</p>
                </motion.div>
              ))}
              <div className="col-span-2"><p className="h-section mb-3 text-xl">Poslední objednávky</p><OrdersList orders={orders.slice(0, 3)} /></div>
            </div>
          )}
          {tab === "objednavky" && <OrdersList orders={orders} />}
          {tab === "faktury" && <OrdersList orders={orders} invoices />}
          {tab === "soubory" && <Downloads list={downloads} />}
          {tab === "udaje" && <Profile />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
